<script setup lang="ts">
  /**
   * CheckboxField.vue
   *
   * A customizable checkbox component that integrates with vee-validate for form handling and validation.
   *
   * Props:
   * - name: string (required) - The name of the checkbox field.
   * - label: string (optional) - The label text for the checkbox.
   * - modelValue: boolean | string | number (optional, default: false) - The initial value of the checkbox.
   * - rules: RuleExpression<any> | undefined (optional) - Validation rules for the checkbox.
   * - checkedValue: boolean | string | number (optional, default: true) - The value when the checkbox is checked.
   * - uncheckedValue: boolean | string | number (optional, default: false) - The value when the checkbox is unchecked.
   *
   * Usage Examples:
   *
   * 1. Basic usage with default true/false values:
   *    <CheckboxField
   *      name="acceptTerms"
   *      label="I accept the terms and conditions"
   *      :rules="yup.boolean().oneOf([true], 'You must agree to the terms')"
   *    />
   *
   * 2. Custom checked/unchecked values:
   *    <CheckboxField
   *      name="subscription"
   *      label="Subscribe to newsletter"
   *      checkedValue="subscribed"
   *      uncheckedValue="unsubscribed"
   *      :rules="yup.string().oneOf(['subscribed'], 'You must be subscribed')"
   *    />
   *
   * 3. Using v-model for two-way binding:
   *    <CheckboxField
   *      v-model="formData.acceptMarketing"
   *      name="acceptMarketing"
   *      label="I agree to receive marketing emails"
   *    />
   *
   * Notes:
   * - The component uses vee-validate's useField for form integration and validation.
   * - If checkedValue and uncheckedValue are not set, it defaults to true/false respectively.
   * - The component supports both boolean and string/number values for checked and unchecked states.
   * - Validation rules can be passed as a Yup schema or as a vee-validate compatible rule object.
   * - The component includes error display and integrates with vee-validate's form handling.
   */

  import { type RuleExpression, useField } from 'vee-validate'
  import { ExclamationCircleIcon } from '@heroicons/vue/24/outline'
  import {
    computed,
    defineProps,
    type PropType,
    useAttrs,
    defineEmits,
    onMounted,
    watch
  } from 'vue'
  import { useThemeColor } from '@/composables/useThemeColorV3'

  const props = defineProps({
    name: {
      type: String,
      required: true
    },
    label: {
      type: String,
      required: false
    },
    modelValue: {
      type: [Boolean, String, Number],
      required: false,
      default: false
    },
    rules: {
      type: Object as PropType<RuleExpression<any> | undefined>,
      required: false
    },
    checkedValue: {
      type: [Boolean, String, Number],
      required: false,
      default: true
    },
    uncheckedValue: {
      type: [Boolean, String, Number],
      required: false,
      default: false
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    dataCy: {
      type: String,
      required: false
    }
  })

  const emit = defineEmits(['update:modelValue'])

  const { value, errorMessage, handleBlur, meta, errors } = useField(props.name, props.rules, {
    initialValue: props.modelValue
  })

  const themeColor = computed(() => useThemeColor().value)

  const errorId = computed(() => `${props.name}-error`)
  const ariaDescribedBy = computed(() => {
    return errorMessage.value ? errorId.value : undefined
  })

  // Ensure the field value stays in sync with the prop
  watch(
    () => props.modelValue,
    (newVal) => {
      if (value.value !== newVal) {
        value.value = newVal
      }
    },
    { immediate: true }
  )

  const isChecked = computed({
    get: () => {
      // Always use the prop value directly for the checked state
      return props.modelValue === props.checkedValue
    },
    set: (newValue: boolean) => {
      const newModelValue = newValue ? props.checkedValue : props.uncheckedValue
      value.value = newModelValue
      emit('update:modelValue', newModelValue)
    }
  })

  const handleChange = (event: Event) => {
    const target = event.target as HTMLInputElement
    // This will trigger the emit('update:modelValue') in the setter
    isChecked.value = target.checked

    // Also update the vee-validate field value
    value.value = target.checked ? props.checkedValue : props.uncheckedValue
  }

  const handleLabelClick = (event: Event) => {
    // Prevent the default label behavior (which would toggle the checkbox)
    event.preventDefault()

    if (!props.disabled) {
      // Toggle the checkbox value
      const newCheckedState = !isChecked.value
      isChecked.value = newCheckedState

      // Also update the vee-validate field value
      value.value = newCheckedState ? props.checkedValue : props.uncheckedValue
    }
  }

  onMounted(() => {
    // this is needed only initially when this component is loaded
    // used to sync up with the 'update:modelValue' emission
    if (props.modelValue === props.checkedValue) {
      isChecked.value = true
    }
  })
</script>

<template>
  <div>
    <div class="flex items-center">
      <input
        :id="props.name"
        :class="[
          `h-5 w-5 rounded border-gray-300 focus:ring-${themeColor}-500`,
          disabled
            ? `cursor-not-allowed bg-${themeColor}-300`
            : `cursor-pointer text-${themeColor}-600`
        ]"
        :checked="isChecked"
        type="checkbox"
        :name="props.name"
        :disabled="disabled"
        :data-cy="dataCy"
        :aria-invalid="!!errorMessage || undefined"
        :aria-describedby="ariaDescribedBy"
        @change="handleChange"
        @blur="handleBlur"
      />
      <label
        v-if="label"
        class="ml-2 block select-none text-sm font-medium"
        :class="{ 'opacity-50': disabled }"
        :for="props.name"
        @click="handleLabelClick"
      >
        {{ label }}
      </label>
    </div>
    <div
      v-if="errorMessage"
      :id="errorId"
      class="mt-1 flex items-center"
      :class="{ 'opacity-50': disabled }"
      aria-live="polite"
    >
      <ExclamationCircleIcon
        class="mr-1 h-5 w-5 text-red-500"
        aria-hidden="true"
      />
      <p class="text-xs text-red-600">
        {{ errorMessage }}
      </p>
    </div>
  </div>
</template>
